import { useSnackbar } from 'notistack';
import { useEffect, useState, useCallback } from 'react';
import { Table, Tag, Button as AntButton, Switch } from 'antd';
import { Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import AddSurvivorshipModal from './survivorships-components/AddSurvivorshipModal';
import { getSurvivorshipRulesByEntityId, updateActiveSurvivorshipStatus } from "../../../api's/DataModelApi";

const SurvivorshipTab = ({ survivorship, entityId, attributes }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [survivorshipRules, setSurvivorshipRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);

  const fetchSurvivorshipRules = useCallback(async () => {
    if (!entityId) return;
    try {
      setLoading(true);
      const response = await getSurvivorshipRulesByEntityId(entityId);
      if (response.status === 200) {
        setSurvivorshipRules(response.data);
      }
    } catch (error) {
      console.error('Error fetching survivorship rules:', error);
      enqueueSnackbar('Failed to load survivorship rules', { variant: 'error' });
      setSurvivorshipRules([]);
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    fetchSurvivorshipRules();
  }, [fetchSurvivorshipRules]);

  const handleActiveStatus = async (id) => {
    setLoading(true);
    try {
      const response = await updateActiveSurvivorshipStatus(id);
      if (response.status === 200) {
        enqueueSnackbar('Status update!', { variant: 'success' });
        await fetchSurvivorshipRules();
      }
    } catch (error) {
      enqueueSnackbar('Failed to update status!', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'survivorshipRuleName',
      key: 'survivorshipRuleName',
      sorter: (a, b) => a.survivorshipRuleName.localeCompare(b.survivorshipRuleName),
      defaultSortOrder: 'ascend',
      render: (value) => <b>{value}</b>,
    },
    {
      title: 'Description',
      dataIndex: 'survivorshipRuleDescription',
      key: 'survivorshipRuleDescription',
      ellipsis: true,
    },
    {
      title: 'Attribute',
      key: 'attribute',
      render: (_, record) => record.ruleCondition?.attributeName || '-',
    },
    {
      title: 'Match Type',
      key: 'matchType',
      render: (_, record) => <Tag color="purple">{record.ruleCondition?.matchType}</Tag>,
    },
    {
      title: 'Active',
      dataIndex: 'ruleStatus',
      key: 'ruleStatus',
      render: (value, record) => (
        <Switch
          checked={!!value}
          checkedChildren="Yes"
          unCheckedChildren="No"
          onChange={() => handleActiveStatus(record.id)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <AntButton
          type="default"
          size="small"
          onClick={() => {
            setSelectedRule(record); // store rule object
            setOpenModal(true);
          }}
        >
          Edit
        </AntButton>
      ),
    },
  ];

  return (
    <div style={{ padding: '0 16px 16px 16px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '500', color: '#333' }}>Survivorship Rules</h3>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          onClick={() => {
            setSelectedRule(null);
            setOpenModal(true);
          }}
        >
          Add Rule
        </Button>
      </div>
      <Table
        dataSource={survivorshipRules}
        loading={loading}
        rowKey={(record) => record.id || record._id}
        bordered
        size="small"
        pagination={{
          defaultPageSize: 5,
          showSizeChanger: true,
          pageSizeOptions: ['5', '10', '20', '50', '100'],
        }}
        columns={columns}
      />
      {openModal && (
        <AddSurvivorshipModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          attributes={attributes}
          entityId={entityId}
          fetchSurvivorshipRules={fetchSurvivorshipRules}
          selectedRule={selectedRule}
        />
      )}
    </div>
  );
};

export default SurvivorshipTab;
